import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { useShareBaseUrl, buildShareUrl } from "@/lib/share";
import { AppFrame, TopBar, WhatsAppFAB } from "@/components/Shell";
import { Phone, Share2, Clock } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [settings, setSettings] = useState(null);
  const shareBase = useShareBaseUrl();

  useEffect(() => {
    (async () => {
      const s = await api.get("/settings"); setSettings(s.data);
      const p = await api.get(`/products/${id}`);
      setProduct(p.data);
    })();
  }, [id]);

  if (!product) {
    return (
      <AppFrame>
        <TopBar title="Loading..." subtitle="Product" />
        <div className="px-5 mt-8 animate-pulse space-y-4">
          <div className="aspect-[4/5] bg-zinc-100 rounded-3xl" />
          <div className="h-4 w-2/3 bg-zinc-100 rounded" />
          <div className="h-4 w-1/3 bg-zinc-100 rounded" />
        </div>
      </AppFrame>
    );
  }

  const images = product.images?.length ? product.images : [
    "https://images.unsplash.com/photo-1705326454924-f6777522b030?w=800&fit=crop",
  ];
  const hasMrp = product.mrp && product.mrp > product.selling_price;
  const discount = hasMrp ? Math.round(((product.mrp - product.selling_price) / product.mrp) * 100) : 0;

  const waNum = (settings?.whatsapp_number || "").replace(/[^0-9]/g, "");
  const isLowStock =
    product.availability === "in_stock" &&
    typeof product.stock_quantity === "number" &&
    product.stock_quantity > 0 &&
    product.stock_quantity <= 5;
  const enquireMsg = `Hello, I am interested in ${product.name}. Is it available?`;
  const reserveMsg = `Hi! I'd like to *reserve ${product.name}* (only ${product.stock_quantity} left). Can you hold one for me?`;
  const activeMsg = isLowStock ? reserveMsg : enquireMsg;
  const waHref = `https://wa.me/${waNum}?text=${encodeURIComponent(activeMsg)}`;
  const telHref = `tel:${settings?.whatsapp_number || ""}`;

  const share = async () => {
    // Always use the configured public domain when sharing to customers,
    // even if staff are browsing via the preview / admin URL.
    const url = product.slug
      ? buildShareUrl(shareBase, `/p/${product.slug}`)
      : window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: product.name, text: `Check out ${product.name}`, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    }
  };

  return (
    <AppFrame>
      <TopBar
        title={product.name}
        subtitle={product.category_name || "Product"}
        right={
          <button
            data-testid="product-share-btn"
            onClick={share}
            className="w-9 h-9 rounded-full grid place-items-center border border-black/10 press"
            aria-label="Share"
          >
            <Share2 size={16} strokeWidth={1.6} />
          </button>
        }
      />

      {/* Image */}
      <div className="px-5 mt-4">
        <div className="aspect-[4/5] bg-zinc-50 rounded-3xl overflow-hidden relative">
          <img
            src={resolveImage(images[imgIdx])}
            alt={product.name}
            className="w-full h-full object-cover"
          />
          {hasMrp && (
            <div className="absolute top-3 left-3 bg-white/90 backdrop-blur text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full">
              {discount}% off
            </div>
          )}
          {isLowStock && (
            <div
              data-testid="pd-low-stock-badge"
              className="absolute top-3 right-3 bg-neutral-900/90 backdrop-blur text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full text-white flex items-center gap-1"
            >
              <Clock size={10} strokeWidth={2} /> Only {product.stock_quantity} left
            </div>
          )}
        </div>
        {images.length > 1 && (
          <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
            {images.map((im, i) => (
              <button
                key={i}
                data-testid={`thumb-${i}`}
                onClick={() => setImgIdx(i)}
                className={`w-16 h-16 shrink-0 rounded-xl overflow-hidden border ${i === imgIdx ? "border-neutral-900" : "border-black/10"} press`}
              >
                <img src={resolveImage(im)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="px-5 mt-6">
        {product.brand && <div className="text-[9px] tracking-editorial-sm uppercase" style={{ color: "var(--ink-3)" }}>{product.brand}</div>}
        <h1 data-testid="product-name" className="font-display text-[28px] leading-[1.05] mt-1.5" style={{ color: "var(--ink)" }}>{product.name}</h1>
        <div className="mt-3 flex items-baseline gap-3">
          <span data-testid="product-price" className="font-display-italic text-[28px]" style={{ color: "var(--accent)" }}>₹{Math.round(product.selling_price)}</span>
          {hasMrp && <span className="text-sm line-through" style={{ color: "var(--ink-3)" }}>₹{Math.round(product.mrp)}</span>}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${product.availability === "in_stock" ? "bg-emerald-600" : "bg-red-600"}`} />
          <span className="text-[11px] tracking-editorial-sm uppercase" style={{ color: "var(--ink-2)" }}>{product.availability === "in_stock" ? "Available in store" : "Out of stock"}</span>
        </div>

        {product.description && (
          <p className="mt-5 text-sm text-neutral-700 leading-relaxed">{product.description}</p>
        )}

        {/* Meta */}
        <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3">
          {[
            ["Category", product.category_name],
            ["Shade", product.shade],
            ["Color", product.color],
            ["Size", product.size],
            ["Net Qty", product.net_quantity],
            ["Batch No.", product.batch_number],
            ["Expiry", product.expiry_date],
            ["MFG Date", product.manufacturing_date],
            ["Code", product.sku || product.barcode],
          ]
            .filter(([_, v]) => !!v)
            .map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{k}</div>
                <div className="text-sm text-neutral-900 mt-0.5">{v}</div>
              </div>
            ))}
        </div>

        {/* CTA row */}
        <div className="mt-8 grid grid-cols-4 gap-3">
          <a
            data-testid="product-call-btn"
            href={telHref}
            className="col-span-1 rounded-full border border-black/10 h-12 grid place-items-center press"
            aria-label="Call"
          >
            <Phone size={18} strokeWidth={1.6} />
          </a>
          <a
            data-testid="product-whatsapp-btn"
            href={waHref}
            target="_blank" rel="noopener noreferrer"
            className={`col-span-3 rounded-full h-12 grid place-items-center press text-[12px] tracking-editorial-sm uppercase shadow-lg ${isLowStock ? "text-white shadow-black/25" : "bg-[#25D366] text-white shadow-green-500/25"}`}
            style={isLowStock ? { background: "var(--ink)" } : undefined}
          >
            {isLowStock ? `Reserve on WhatsApp` : `Enquire on WhatsApp`}
          </a>
        </div>
        <div className="h-12" />
      </div>

      <WhatsAppFAB number={settings?.whatsapp_number} message={`Hello, I am interested in ${product.name}.`} />
    </AppFrame>
  );
}
